import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Image as ImageIcon, Upload, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FlyerPreview from '@/components/flyer/FlyerPreview';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Sensible defaults built from Casa Samba's live data
const DEFAULT_HERO =
  'https://www.casasamba.com/wp-content/uploads/2022/03/NASPA-Parade-high-3.gif';

const FlyerGenerator = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroImage, setHeroImage] = useState(DEFAULT_HERO);
  const [galleryImages, setGalleryImages] = useState([]); // up to 3 accents
  const [exporting, setExporting] = useState(false);
  const flyerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/classes`);
        setClasses(Array.isArray(res.data) ? res.data.filter((c) => c.is_active !== false) : []);
      } catch {
        setClasses([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Convert uploaded File to a data URL so it renders inside html2canvas without CORS
  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setHeroImage(dataUrl);
      toast.success('Hero image updated');
    } catch {
      toast.error('Could not read image');
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    if (files.length === 0) return;
    try {
      const urls = await Promise.all(files.map(readFileAsDataUrl));
      setGalleryImages(urls);
      toast.success(`${urls.length} photo${urls.length > 1 ? 's' : ''} added`);
    } catch {
      toast.error('Could not read one of the images');
    }
  };

  const exportImage = async (format) => {
    if (!flyerRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(flyerRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `casa-samba-classes-flyer-${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } else {
        const imgData = canvas.toDataURL('image/png');
        // Match the flyer aspect ratio (8.5x11 letter) — a 850x1100 element at scale=2
        const pdf = new jsPDF({ unit: 'px', format: [850, 1100], orientation: 'portrait' });
        pdf.addImage(imgData, 'PNG', 0, 0, 850, 1100);
        pdf.save(`casa-samba-classes-flyer-${Date.now()}.pdf`);
      }
      toast.success(`Flyer downloaded as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Export failed — check the console for details');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="text-white/60 hover:text-yellow-400"
              data-testid="flyer-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-black">Class Flyer Generator</h1>
              <p className="text-sm text-white/60">
                Downloadable one-page flyer using your live class schedule.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => exportImage('png')}
              disabled={exporting}
              data-testid="flyer-download-png"
              className="px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PNG
            </button>
            <button
              onClick={() => exportImage('pdf')}
              disabled={exporting}
              data-testid="flyer-download-pdf"
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PDF
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* Controls */}
          <div className="space-y-4">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
              <h3 className="text-sm font-bold uppercase tracking-wider text-yellow-400 mb-3">
                Hero Photo
              </h3>
              <label
                htmlFor="flyer-hero-upload"
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-white/20 hover:border-yellow-400 hover:bg-white/5 cursor-pointer text-center transition-colors"
              >
                <Upload className="w-6 h-6 text-white/60" />
                <span className="text-xs text-white/70">Upload a hero photo</span>
                <span className="text-[10px] text-white/40">PNG/JPG up to ~10MB</span>
              </label>
              <input
                id="flyer-hero-upload"
                type="file"
                accept="image/*"
                onChange={handleHeroUpload}
                className="hidden"
                data-testid="flyer-hero-upload"
              />
              {heroImage && (
                <img
                  src={heroImage}
                  alt="Current hero"
                  className="mt-3 w-full h-24 object-cover rounded-lg"
                />
              )}
              <button
                onClick={() => setHeroImage(DEFAULT_HERO)}
                className="mt-2 w-full text-xs text-white/50 hover:text-yellow-400 flex items-center justify-center gap-1"
              >
                <RefreshCw className="w-3 h-3" /> Reset to Casa Samba parade photo
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
              <h3 className="text-sm font-bold uppercase tracking-wider text-yellow-400 mb-3">
                Extra Photos <span className="text-white/40 font-normal">(up to 3)</span>
              </h3>
              <label
                htmlFor="flyer-gallery-upload"
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-white/20 hover:border-yellow-400 hover:bg-white/5 cursor-pointer text-center transition-colors"
              >
                <ImageIcon className="w-6 h-6 text-white/60" />
                <span className="text-xs text-white/70">Upload 1–3 accent photos</span>
              </label>
              <input
                id="flyer-gallery-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handleGalleryUpload}
                className="hidden"
                data-testid="flyer-gallery-upload"
              />
              {galleryImages.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-1.5">
                  {galleryImages.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Accent ${i + 1}`}
                      className="w-full h-14 object-cover rounded"
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 rounded-2xl bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-100/80 leading-relaxed">
              <strong className="block text-yellow-300 mb-1">Auto-filled</strong>
              Price, phone, address, and schedule come straight from your live class data. Add or
              edit classes at <Link to="/admin/dashboard" className="underline">Admin → Classes</Link>.
            </div>
          </div>

          {/* Live Preview */}
          <div className="overflow-x-auto">
            <div className="mx-auto" style={{ width: 850 }}>
              {loading ? (
                <div className="flex items-center gap-2 text-white/60 py-10 justify-center">
                  <Loader2 className="w-5 h-5 animate-spin" /> Loading class schedule...
                </div>
              ) : (
                <FlyerPreview
                  ref={flyerRef}
                  heroImage={heroImage}
                  galleryImages={galleryImages}
                  classes={classes}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlyerGenerator;
